import {
  EntityProjectionWithSelf,
  Link,
  PagingCollectionProjection,
} from 'app/driver-pay/persistence/implementation/DriverPayBaseEntity';
import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { environment } from 'environments/environment';
import { map, Observable, of, share } from 'rxjs';
import { QueryState } from 'app/driver-pay/persistence/core/CollectionView';
import { EntityCrudService, EntityViewService } from 'app/driver-pay/persistence/core/EntityService';
import { parseTemplate } from 'url-template';
import { FormBuilder, Validators } from '@angular/forms';
import { EntityFormGroup } from 'app/driver-pay/persistence/core/EntityView';

interface RequestOpts {
  method: string;
  path: string;
  subPath: string;
  params: HttpParams;
  body?: any;
}

@Injectable({
  providedIn: 'root',
})
export class RequestHelper {
  protected httpClient = inject(HttpClient);
  protected defaultRequestOpts = (): RequestOpts => {
    return {
      path: '',
      method: 'GET',
      subPath: '',
      params: new HttpParams(),
    };
  };

  public request<T>(inputOpts: Partial<RequestOpts> = this.defaultRequestOpts()): Observable<T> {
    const opts = {
      ...this.defaultRequestOpts(),
      ...inputOpts,
    };
    return this.httpClient.request<T>(opts.method, `${opts.path}${opts.subPath}`, {
      context: new HttpContext(),
      params: opts.params,
      body: opts.body,
      responseType: 'json',
      withCredentials: true,
      observe: 'body',
      transferCache: true,
      reportProgress: false,
    });
  }
}

export abstract class EntityViewServiceImpl<CollectionProjection, IdType, ItemProjection> implements EntityViewService<
  CollectionProjection,
  IdType,
  ItemProjection
> {
  protected requestHelper = inject(RequestHelper);
  protected static basePath = environment.api.baseUrl;
  protected abstract controllerPath: string;
  protected fetchCollectionPath?: string; // set this to override the controller path for the get collection operation
  protected abstract getCollectionFromPagedModel: (
    pagedModel: PagingCollectionProjection<CollectionProjection>
  ) => CollectionProjection[];
  protected collectionProjection?: string;
  protected itemProjection?: string;

  abstract getIdFromCollectionProjection: (entity: CollectionProjection) => IdType;
  abstract getUriForItem: (id: IdType) => string;

  fetchCollection(queryState: QueryState, pageIndex?: number, pageSize?: number) {
    if (pageIndex !== undefined && pageIndex !== null) {
      queryState.setSingular('page', pageIndex);
    }
    if (pageSize !== undefined && pageSize !== null) {
      queryState.setSingular('size', pageSize);
    }
    if (this.collectionProjection) {
      queryState.setSingular('projection', this.collectionProjection);
    }

    return this.requestHelper
      .request<PagingCollectionProjection<CollectionProjection>>({
        method: 'get',
        path: `${EntityViewServiceImpl.basePath}${this.fetchCollectionPath ?? this.controllerPath}`,
        params: queryState.toHttpParams(),
      })
      .pipe(
        map((res) => {
          return {
            entities: this.getCollectionFromPagedModel(res),
            page: res.page,
          };
        })
      )
      .pipe(share());
  }

  fetchItem(item: IdType): Observable<ItemProjection> {
    let params = new HttpParams();
    if (this.itemProjection) {
      params = params.set('projection', this.itemProjection);
    }
    return this.requestHelper.request<ItemProjection>({
      method: 'get',
      path: this.getUriForItem(item),
      params,
    });
  }
}

export abstract class EntityServiceImpl<CollectionProjection, IdType, ItemProjection, ResponseProjection, RequestBody>
  extends EntityViewServiceImpl<CollectionProjection, IdType, ItemProjection>
  implements EntityCrudService<CollectionProjection, IdType, ItemProjection, RequestBody>
{
  abstract populateRequestBodyFromItemProjection: (item: ItemProjection) => Partial<RequestBody>;
  abstract getIdFromResponseProjection: (entity: ResponseProjection) => IdType;
  abstract getIdFromItemProjection: (entity: ItemProjection) => IdType;

  createItem(item: RequestBody) {
    return this.requestHelper
      .request<ResponseProjection>({
        method: 'post',
        path: `${EntityViewServiceImpl.basePath}${this.controllerPath}`,
        body: item,
      })
      .pipe(
        map((value) => {
          return this.getIdFromResponseProjection(value);
        })
      );
  }

  updateItem(item: IdType, requestBody: Partial<RequestBody>): Observable<IdType> {
    return this.requestHelper
      .request<ResponseProjection>({
        method: 'patch',
        path: this.getUriForItem(item),
        body: requestBody,
      })
      .pipe(
        map((value) => {
          return this.getIdFromResponseProjection(value);
        })
      );
  }

  deleteItem(item: IdType): Observable<void> {
    return this.requestHelper
      .request({
        method: 'delete',
        path: this.getUriForItem(item),
      })
      .pipe(
        map(() => {
          return;
        })
      );
  }
}

export abstract class DriverPayBaseEntityViewServiceImpl<
  CollectionProjection extends EntityProjectionWithSelf,
  ItemProjection extends EntityProjectionWithSelf,
> extends EntityViewServiceImpl<CollectionProjection, Link, ItemProjection> {
  public static extractUuid(href: string, controllerPath: string) {
    const length = controllerPath.length + 1;
    return new URL(href).pathname.substring(length, length + 36);
  }

  public static rewriteUrlWithLocalProxy(link: Link): Link {
    const url = new URL(parseTemplate(link.href).expand({}));
    return { href: `${EntityViewServiceImpl.basePath}${url.pathname}${url.search}` };
  }

  getIdFromCollectionProjection = (entity: CollectionProjection) => entity._links.self;

  getUriForItem = (id: Link) =>
    parseTemplate(DriverPayBaseEntityViewServiceImpl.rewriteUrlWithLocalProxy(id).href).expand({});
}

export abstract class CollectionAsItemViewServiceImpl<Projection> extends EntityViewServiceImpl<
  Projection,
  Projection,
  Projection
> {
  getIdFromCollectionProjection = (entity: Projection) => entity;

  getUriForItem = (id: Projection) => {
    throw new Error('Not Implemented');
  };

  override fetchItem(item: Projection) {
    return of(item);
  }
}

export abstract class DriverPayBaseEntityServiceImpl<
  CollectionProjection extends EntityProjectionWithSelf,
  ItemProjection extends CollectionProjection,
  RequestBody,
>
  extends EntityServiceImpl<CollectionProjection, Link, ItemProjection, EntityProjectionWithSelf, RequestBody>
  implements EntityCrudService<CollectionProjection, Link, ItemProjection, RequestBody>
{
  getIdFromCollectionProjection = (entity: CollectionProjection) => entity._links.self;
  getIdFromItemProjection = (entity: ItemProjection) => entity._links.self;
  getIdFromResponseProjection = (entity: EntityProjectionWithSelf) => entity._links.self;
  getUriForItem = (id: Link) =>
    parseTemplate(DriverPayBaseEntityViewServiceImpl.rewriteUrlWithLocalProxy(id).href).expand({});
}

export abstract class CollectionAsItemServiceImpl<
  Projection,
  IdType,
  ResponseType,
  RequestBody,
> extends EntityServiceImpl<Projection, IdType | Projection, Projection, ResponseType, RequestBody> {
  getIdFromCollectionProjection = (entity: Projection) => entity;
  override fetchItem(item: IdType | Projection) {
    return of(item as Projection);
  }
}

@Injectable({
  providedIn: 'root',
})
export class LinkFormGroupProvider {
  private readonly formBuilder = inject(FormBuilder);

  linkFormGroup(): EntityFormGroup<Link> {
    return this.formBuilder.group({
      href: [null as string | null, Validators.required],
      hreflang: [null as string | null],
      title: [null as string | null],
      type: [null as string | null],
      deprecation: [null as string | null],
      profile: [null as string | null],
      name: [null as string | null],
      templated: [false as boolean | null],
    });
  }
}
